import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { FieldPath } from 'react-hook-form';
import * as yup from 'yup';
import { useUserIdField } from '../../../data-fields/CommonFields';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { field2ToFormField, FieldEntity } from '../../../data-models/field2.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { companyFields } from '../../../schemas/Company.schema';
import { columnsRawUnwrapped } from '../../../services/state/AdminPanel/ColumnDefinitionsState';
import { customFieldsByEntityAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { ColumnMeta } from '../../../types';
import { schemaToFormFields } from '../../../util/schema-utils';
import { filterAndSortByPreferences } from '../utils/filterAndSortByPreferences';

export function useAllCompanyDetailsFields() {
  const companyDetailsSchema = useCompanyDetailsPanelSchema();
  const customFields = useAtomValue(customFieldsByEntityAtom);

  return useMemo(() => {
    const companyCustomFields =
      customFields.get(FieldEntity.company)?.map((field) => field2ToFormField(field)) ?? [];
    const companyFields = schemaToFormFields(companyDetailsSchema);
    return companyFields.concat(companyCustomFields);
  }, [companyDetailsSchema, customFields]);
}

export function useCompanyDetailsFields(fieldsToShow?: FieldPath<ICompanyDataModel>[]) {
  const allDetailsFields = useAllCompanyDetailsFields();

  return useMemo(() => {
    if (!fieldsToShow?.length) {
      return allDetailsFields;
    }
    return filterAndSortByPreferences(allDetailsFields, fieldsToShow);
  }, [allDetailsFields, fieldsToShow]);
}

// Configs reference the data point as a metrics path or a company-prefixed path.
const IMPLIED_EQUITY_COLUMN_PATHS = new Set(['impliedEquityValue', 'company.impliedEquityValue']);

// FD-3700: the Company Views grid formats this data point via its column config
// (ColumnMeta.formatting); resolve the same id so both surfaces render identically.
// Only the Return Forecast view configures this data point.
export function getImpliedEquityFormatterId(columns: ColumnMeta[]): string | undefined {
  const column = columns.find(
    (c) =>
      c.viewType === COMPANY_VIEW_TYPE.RETURN_FORECAST &&
      IMPLIED_EQUITY_COLUMN_PATHS.has(c.path) &&
      c.formatting
  );
  return column?.formatting || undefined;
}

function useImpliedEquityFormatterId() {
  const columns = useAtomValue(columnsRawUnwrapped);
  return getImpliedEquityFormatterId(columns);
}

function useCompanyDetailsPanelSchema() {
  const {
    acquirer,
    boardDirectorId,
    dealLeadId,
    dealTeamIds,
    internalSourceId,
    boardStatus,
    impliedEquityValue,
    legalName,
  } = companyFields();
  const impliedEquityFormatterId = useImpliedEquityFormatterId();

  return yup.object({
    acquirer,
    boardStatus,
    legalName,
    impliedEquityValue: impliedEquityValue.formMeta({
      isCalculated: true,
      ...(impliedEquityFormatterId ? { formatter: impliedEquityFormatterId } : {}),
    }),
    internalSourceId: internalSourceId.formMeta(
      useUserIdField({
        key: 'internalSourceId',
        label: 'Internal Source',
      })
    ),
    dealLeadId: dealLeadId.formMeta(
      useUserIdField({
        key: 'dealLeadId',
        label: 'Deal Lead',
      })
    ),
    boardDirectorId: boardDirectorId.formMeta(
      useUserIdField({
        key: 'boardDirectorId',
        label: 'Board Director',
      })
    ),
    dealTeamIds: dealTeamIds.formMeta(
      useUserIdField({
        key: 'dealTeamIds',
        label: 'Deal Team',
        multi: true,
      })
    ),
  });
}
